import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { Observable, catchError, from, map, of, switchMap, take } from 'rxjs';

import { MassOperationResponse } from 'src/app/api/services/company-api.service';
import {
  WorkspaceObjects,
  WorkspaceObjectsScope,
  injectFetchWorkspaceObjects,
  workspaceObjectsQueriesFilter,
  workspaceObjectsQueriesFilterFor,
} from 'src/app/api/queries/workspace.queries';
import { AppState } from 'src/app/reducers';
import {
  augmentObjectWithIconAndImage,
  augmentObjects,
} from 'src/app/shared/utils/object-utils';

import { NomObjectLifecycleStatus, NomObjectTypes } from '../constants';
import { Dashboard, Dimension, Feed } from '../models/core.models';
import { selectCurrentCompanyId } from '../reducers';

const EMPTY_OBJECTS: WorkspaceObjects = {
  feeds: [],
  dashboards: [],
  dimensions: [],
};

/** Merge fields from `newArray` into the rows of `stateArray` that match on `key`. */
function updateArrayByKey<T>(
  stateArray: T[],
  newArray: T[],
  key: keyof T,
): T[] {
  return stateArray.map((stateItem) => {
    const index = newArray.findIndex(
      (existingItem) => existingItem[key] === stateItem[key],
    );
    return index !== -1 ? { ...stateItem, ...newArray[index] } : stateItem;
  });
}

function upsertById<T extends { id: string }>(list: T[], object: T): T[] {
  const index = list.findIndex((item) => item.id === object.id);
  if (index === -1) {
    return [...list, object];
  }

  const next = [...list];
  next[index] = object;
  return next;
}

function replaceById<T extends { id: string }>(
  list: T[],
  id: string,
  object: T,
): T[] | null {
  const index = list.findIndex((item) => item.id === id);
  if (index === -1) {
    // * not in this list - nothing to replace
    return null;
  }

  const next = [...list];
  next[index] = object;
  return next;
}

/** A feed with `object_type_id === SEARCH_DIMENSION` lives in `dimensions`. */
function isDimension(feed: Feed): boolean {
  return feed.object_type_id === NomObjectTypes.SEARCH_DIMENSION;
}

/**
 * The non-observer side of `injectWorkspaceObjectsQuery`:
 *
 * - **fetch-if-missing** for the widget sidebar's feed picker;
 * - **the cache surgery** every object write needs, called by each write's
 *   owner (the dashboard/object mutation hooks, `FeedPersistenceService`,
 *   `FeedResolver`).
 *
 * Appends are scoped to the object's own `workspace_id`, while replacements, merges
 * and removals are id-based and run over every cached entry.
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceObjectsService {
  private readonly store = inject(Store<AppState>);
  private readonly queryClient = inject(QueryClient);
  private readonly fetchObjects = injectFetchWorkspaceObjects();

  /**
   * The **active** feeds of one workspace, for the widget sidebar's preview-feed
   * picker. Deliberately `ACTIVE` rather than the workspace screen's current
   * filter, which would fill the picker with archived or deleted feeds.
   */
  activeFeeds$(workspaceId: string): Observable<Feed[]> {
    return this.workspaceObjects$(
      workspaceId,
      NomObjectLifecycleStatus.ACTIVE,
    ).pipe(map((objects) => objects.feeds));
  }

  /** Drop every cached workspace-object list. */
  invalidate(): void {
    void this.queryClient.invalidateQueries(workspaceObjectsQueriesFilter());
  }

  /**
   * Feed load (`FeedResolver`) - upsert the feed the user just entered into its workspace's
   * feeds, or dimensions, by object type.
   */
  upsertFeed(feed: Feed): void {
    this.patchWorkspace(feed.workspace_id, (objects) =>
      isDimension(feed)
        ? {
            ...objects,
            dimensions: upsertById(
              objects.dimensions,
              augment(feed) as Dimension,
            ),
          }
        : { ...objects, feeds: upsertById(objects.feeds, augment(feed)) },
    );
  }

  /** Feed edit - replace if present, otherwise do nothing. */
  replaceFeed(feedId: string, feed: Feed): void {
    this.patchEvery((objects) => {
      if (isDimension(feed)) {
        const dimensions = replaceById(
          objects.dimensions,
          feedId,
          augment(feed) as Dimension,
        );
        return dimensions ? { ...objects, dimensions } : objects;
      }

      const feeds = replaceById(objects.feeds, feedId, augment(feed));
      return feeds ? { ...objects, feeds } : objects;
    });
  }

  /**
   * Dashboard update - replace if present, preserving the runtime
   * `selected_page_id`, which the response does not carry.
   */
  replaceDashboard(dashboard: Dashboard): void {
    this.patchEvery((objects) => {
      const current = objects.dashboards.find((d) => d.id === dashboard.id);
      if (!current) {
        return objects;
      }

      const dashboards = replaceById(
        objects.dashboards,
        dashboard.id,
        augment({
          ...dashboard,
          selected_page_id: current.selected_page_id,
        }),
      );
      return dashboards ? { ...objects, dashboards } : objects;
    });
  }

  /**
   * Feed create - append. **Not** augmented, unlike every other write here;
   * the card picks up its icon on the next list fetch.
   */
  appendFeed(feed: Feed): void {
    this.patchWorkspace(feed.workspace_id, (objects) =>
      isDimension(feed)
        ? { ...objects, dimensions: [...objects.dimensions, feed as Dimension] }
        : { ...objects, feeds: [...objects.feeds, feed] },
    );
  }

  /** Feed duplicate - append the copies to their workspace. */
  appendFeeds(feeds: Feed[]): void {
    for (const feed of augmentObjects(feeds ?? []) as Feed[]) {
      this.patchWorkspace(feed.workspace_id, (objects) => ({
        ...objects,
        feeds: [...objects.feeds, feed],
      }));
    }
  }

  /** Dashboard create - append one dashboard to its workspace. */
  appendDashboard(dashboard: Dashboard): void {
    this.patchWorkspace(dashboard.workspace_id, (objects) => ({
      ...objects,
      dashboards: [...objects.dashboards, augment(dashboard)],
    }));
  }

  /** Dashboard duplicate - append the copies to their workspace. */
  appendDashboards(dashboards: Dashboard[]): void {
    for (const dashboard of augmentObjects(dashboards ?? []) as Dashboard[]) {
      this.patchWorkspace(dashboard.workspace_id, (objects) => ({
        ...objects,
        dashboards: [...objects.dashboards, dashboard],
      }));
    }
  }

  /**
   * Move - merge the response's fields, the new `workspace_id` among
   * them, into the rows already cached. The moved row then fails the workspace
   * screen's `workspace_id` filter and leaves the list.
   */
  mergeMovedObjects(response: MassOperationResponse): void {
    this.patchEvery((objects) => ({
      ...objects,
      feeds: updateArrayByKey(objects.feeds, response.data.feeds ?? [], 'id'),
      dimensions: updateArrayByKey(
        objects.dimensions,
        response.data.dimensions ?? [],
        'id',
      ),
    }));
  }

  /**
   * Mass operation - archive / unarchive / delete / undelete take the
   * affected rows out of the list the user is looking at.
   */
  removeObjects(response: MassOperationResponse): void {
    const feedIds = (response.data.feeds ?? []).map((f) => f.id);
    const dashboardIds = (response.data.dashboards ?? []).map((d) => d.id);
    const dimensionIds = (response.data.dimensions ?? []).map((d) => d.id);

    this.patchEvery((objects) => ({
      ...objects,
      feeds: objects.feeds.filter((f) => !feedIds.includes(f.id)),
      dashboards: objects.dashboards.filter(
        (d) => !dashboardIds.includes(d.id),
      ),
      dimensions: objects.dimensions.filter(
        (d) => !dimensionIds.includes(d.id),
      ),
    }));
  }

  /** Run a patch over every cached workspace-object list. */
  private patchEvery(
    patch: (objects: WorkspaceObjects) => WorkspaceObjects,
  ): void {
    this.queryClient.setQueriesData<WorkspaceObjects>(
      workspaceObjectsQueriesFilter(),
      (objects) => (objects ? patch(objects) : objects),
    );
  }

  /** Run a patch over the cached lists of one workspace only. */
  private patchWorkspace(
    workspaceId: string | undefined,
    patch: (objects: WorkspaceObjects) => WorkspaceObjects,
  ): void {
    if (!workspaceId) {
      return;
    }

    this.queryClient.setQueriesData<WorkspaceObjects>(
      workspaceObjectsQueriesFilterFor(workspaceId),
      (objects) => (objects ? patch(objects) : objects),
    );
  }

  private workspaceObjects$(
    workspaceId: string,
    lifecycleStatus: number,
  ): Observable<WorkspaceObjects> {
    return this.store.select(selectCurrentCompanyId).pipe(
      take(1),
      map((companyId): WorkspaceObjectsScope | null =>
        workspaceId ? { workspaceId, companyId, lifecycleStatus } : null,
      ),
      switchMap((scope) =>
        scope ? from(this.fetchObjects(scope)) : of(EMPTY_OBJECTS),
      ),
      // * a failed fetch degrades to empty lists rather than erroring the stream:
      // * the consumer is a `<select>`'s options
      catchError(() => of(EMPTY_OBJECTS)),
    );
  }
}

/** `augmentObjectWithIconAndImage`, keeping the argument's type. */
function augment<T extends Feed | Dashboard>(object: T): T {
  return augmentObjectWithIconAndImage(object) as T;
}
